import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SouthIcon from "@mui/icons-material/South";
import { useSnackbar } from "notistack";

import api from "/imports/api/client";
import { formatMoney } from "../dashboardHelpers";
import SessionCard from "../../PackagesView/UpfrontSessionsView/components/SessionCard";
import { wall, sessionGridSx } from "../../PackagesView/UpfrontSessionsView/upfrontSessionsHelpers";

const dayLabel = (iso) => wall(iso).toFormat("ccc d LLL");

// Same reading order as the session cards: day and time first, in bold, then length,
// coach and — in bold, because clubs run the class at several venues — the location.
// Shared with the lessons page's reschedule confirm.
// `badge` tags a session that is already the client's (the club booked it, the pack is
// about to pay for it), inside the card rather than as a loose line under it.
// `cancelled` tints the card light red: the session that goes, told apart from the one
// that takes its place without reading either.
export function SessionSummary({ label, startDate, time, duration, trainerName, facilityName, locationName, highlight, cancelled, badge }) {
  const meta = [duration ? `${duration} min` : null, trainerName, facilityName].filter(Boolean).join(" · ");
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: (t) => (highlight ? t.palette.primary.main : cancelled ? alpha(t.palette.error.main, 0.4) : t.palette.divider),
        bgcolor: (t) => (highlight ? alpha(t.palette.primary.main, 0.05) : cancelled ? alpha(t.palette.error.main, 0.06) : t.palette.action.hover)
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="caption" color="textSecondary" component="div">
          {label}
        </Typography>
        {badge && (
          <Chip
            size="small"
            label={badge}
            sx={{ height: "1.25rem", fontSize: "0.65rem", fontWeight: 700, color: "primary.dark", bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }}
          />
        )}
      </Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {wall(startDate).toFormat("cccc d LLLL")} · {time}
      </Typography>
      <Typography variant="caption" color="textSecondary" component="div">
        {meta}
        {locationName && (
          <>
            {meta ? " · " : ""}
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
              {locationName}
            </Box>
          </>
        )}
      </Typography>
    </Box>
  );
}

// The club changed the original booking while the client was moving it (removed them,
// cancelled it at the desk). Nothing was moved, so the list is stale — close and refresh.
const isBookingChanged = (err) => err?.data?.data?.reason === "BOOKING_CHANGED";

function RescheduleDialog({ open, booking, slug, currency, onClose, onRescheduled, onBookingChanged }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getRescheduleOptions(slug, booking._id));
    } catch (err) {
      if (isBookingChanged(err)) {
        enqueueSnackbar(err.message, { variant: "warning" });
        onBookingChanged();
        return;
      }
      setError(err.message || "Couldn't load the available sessions");
    } finally {
      setLoading(false);
    }
  };

  // One dialog serves every booking in the list — start clean on each open.
  useEffect(() => {
    if (!open || !booking) return;
    setData(null);
    setSelected(null);
    setSubmitting(false);
    load();
  }, [open, booking?._id]);

  const loadLater = async () => {
    setLoadingMore(true);
    try {
      const more = await api.getRescheduleOptions(slug, booking._id, { page: data.page + 1 });
      const known = new Set(data.options.map((o) => o._id));
      setData({
        ...more,
        options: [...data.options, ...more.options.filter((o) => !known.has(o._id))]
      });
    } catch (err) {
      enqueueSnackbar(err.message || "Couldn't load more sessions", { variant: "error" });
    } finally {
      setLoadingMore(false);
    }
  };

  const byMonth = useMemo(() => {
    const groups = new Map();
    (data?.options || []).forEach((o) => {
      const month = wall(o.startDate).toFormat("LLLL yyyy");
      groups.set(month, [...(groups.get(month) || []), o]);
    });
    return [...groups.entries()];
  }, [data]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const result = await api.rescheduleBooking(slug, booking._id, { targetBookingId: selected._id });
      onRescheduled({ result, label: `${wall(selected.startDate).toFormat("cccc d LLLL")} at ${selected.time}` });
    } catch (err) {
      setSubmitting(false);
      if (isBookingChanged(err)) {
        enqueueSnackbar(err.message, { variant: "warning" });
        onBookingChanged();
        return;
      }
      enqueueSnackbar(err.message || "Couldn't move your booking", { variant: "error" });
      // The session filled up or changed while the client was deciding: show the list as it is now.
      if (err.status === 409 || err.status === 422) {
        setSelected(null);
        load();
      }
    }
  };

  const busy = submitting;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pr: 1.5 }}>
        {selected ? "Confirm new session" : "Reschedule booking"}
        <IconButton onClick={onClose} disabled={busy} aria-label="Close">
          <CloseIcon sx={{ fontSize: "1.25rem" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {booking && !selected && (
          <Box mb={2.5}>
            <SessionSummary
              label={`Currently booked — ${booking.serviceName}`}
              startDate={booking.startDate}
              time={booking.time}
              duration={booking.duration}
              trainerName={booking.trainerName}
              locationName={booking.location}
            />
          </Box>
        )}

        {loading ? (
          <Box display="flex" alignItems="center" gap={2} py={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Finding sessions with a free place…
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : !data || !booking ? null : selected ? (
          <Box>
            <SessionSummary
              label="From · cancelled"
              startDate={booking.startDate}
              time={booking.time}
              duration={booking.duration}
              trainerName={booking.trainerName}
              locationName={booking.location}
              cancelled
            />
            <Box display="flex" justifyContent="center" py={0.5}>
              <SouthIcon sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
            </Box>
            <Box mb={2}>
              <SessionSummary
                label="To"
                startDate={selected.startDate}
                time={selected.time}
                duration={selected.duration}
                trainerName={selected.trainer?.name}
                locationName={selected.location?.name}
                badge={selected.ownBooking ? "Booked for you · not yet paid" : undefined}
                highlight
              />
            </Box>

            {/* The new booking is paid from the same source as the old one, never a mix. */}
            <Alert severity="success" variant="outlined">
              {data.mode === "MONEY_MOVE"
                ? `The ${formatMoney(data.creditAmount, currency)} you paid moves with you — it goes to your wallet and pays for this ${
                    selected.ownBooking ? "session the club already booked for you" : "session"
                  }. There's nothing to pay.`
                : selected.ownBooking
                  ? `Your ${data.pack?.name ? `"${data.pack.name}"` : "package"} session moves with you: it pays for this session the club already booked for you. There's nothing to pay.`
                  : `Your ${data.pack?.name ? `"${data.pack.name}"` : "package"} session moves with you — there's nothing to pay.`}
            </Alert>

            <Typography variant="body2" color="textSecondary" mt={2}>
              Your current booking is cancelled only once your place in the new session is confirmed.
            </Typography>
          </Box>
        ) : (
          <Box>
            {byMonth.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No other session of this class has a free place you can move to
                {data.range ? ` between ${dayLabel(data.range.dateStart)} and ${dayLabel(data.range.dateEnd)}` : ""}.
                {data.mode === "MONEY_MOVE"
                  ? " You can move to a session of the same length and price as the one you paid for."
                  : " You can move to a session of the same length that your package covers."}
              </Typography>
            ) : (
              byMonth.map(([month, options]) => (
                <Box key={month} mb={2.5}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 700 }}>
                    {month}
                  </Typography>
                  <Box sx={sessionGridSx}>
                    {options.map((o) => (
                      <SessionCard
                        key={o._id}
                        // The client's own seat the club booked and never checked out: the pack
                        // pays for it, so its spot count means nothing here.
                        session={o.ownBooking ? { ...o, spotsAvailable: null } : o}
                        reason={o.ownBooking ? "Booked for you" : undefined}
                        isPicked={selected?._id === o._id}
                        onToggle={() => setSelected(o)}
                      />
                    ))}
                  </Box>
                </Box>
              ))
            )}
            {data.page < data.pages && (
              <Box display="flex" justifyContent="center" mt={1}>
                <Button
                  size="small"
                  onClick={loadLater}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={14} /> : <ArrowDownwardIcon sx={{ fontSize: "1rem" }} />}
                  sx={{ textTransform: "none" }}
                >
                  Show more sessions
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {selected ? (
          <>
            <Button onClick={() => setSelected(null)} disabled={busy}>
              Back
            </Button>
            <Button variant="contained" onClick={confirm} disabled={busy} sx={{ color: "common.white" }}>
              {busy ? <CircularProgress size={18} /> : "Confirm move"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RescheduleDialog;
